import { useState } from "react";
import {
  Alert,
  FormControlLabel,
  FormGroup,
  Switch,
  Typography,
} from "@mui/material";
import type { Ebook, ReaderControls } from "@ebook/contracts";
import { api } from "../lib/api-client";
import { useTranslate } from "../i18n";

type BooleanControlKey =
  | "showMenu"
  | "showSearch"
  | "showPdfDownload"
  | "showPagination"
  | "showZoom"
  | "showFullscreen";
const options: Array<{
  key: BooleanControlKey;
  label: string;
  description: string;
}> = [
  { key: "showMenu", label: "แสดงเมนู", description: "สารบัญจาก PDF และภาพย่อทุกหน้า" },
  { key: "showSearch", label: "แสดงการค้นหา", description: "ค้นหาจากข้อความที่ extract หรือแก้ไขไว้" },
  { key: "showPdfDownload", label: "แสดงปุ่มดาวน์โหลด PDF", description: "ซ่อนเฉพาะปุ่ม ไม่เปลี่ยนสิทธิ์เข้าถึงไฟล์" },
  { key: "showPagination", label: "แสดง Pagination", description: "ปุ่มก่อนหน้า เลขหน้า และปุ่มถัดไป" },
  { key: "showZoom", label: "แสดง Zoom", description: "ย่อและขยายหน้าหนังสือ 50–200%" },
  { key: "showFullscreen", label: "แสดง Fullscreen", description: "เปิด Reader เต็มหน้าจอเมื่อ browser รองรับ" },
];

export function ReaderToolbarSettings({
  ebook,
  onChange,
}: {
  ebook: Ebook;
  onChange: (ebook: Ebook) => void;
}) {
  const [saving, setSaving] = useState<keyof ReaderControls | null>(null);
  const [error, setError] = useState("");
  const __ = useTranslate();

  async function saveControls(nextControls: ReaderControls, savingKey: keyof ReaderControls) {
    const previous = ebook;
    const next = {
      ...ebook,
      readerControls: nextControls,
    };
    onChange(next);
    setSaving(savingKey);
    setError("");
    try {
      onChange(await api.updateReaderControls(ebook.id, next.readerControls));
    } catch (caught) {
      onChange(previous);
      setError(caught instanceof Error ? caught.message : __("บันทึกการตั้งค่าไม่สำเร็จ"));
    } finally {
      setSaving(null);
    }
  }

  async function toggle(key: BooleanControlKey, checked: boolean) {
    await saveControls({ ...ebook.readerControls, [key]: checked }, key);
  }

  return <>
    <Typography variant="body2" color="text.secondary" mb={2}>
      {__("เลือกเครื่องมือที่ผู้อ่านจะเห็นใน Preview, ZIP และเว็บไซต์ที่ Publish")}
    </Typography>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    <FormGroup>
      {options.map((option) => (
        <FormControlLabel
          key={option.key}
          sx={{ alignItems: "flex-start", py: 1 }}
          control={<Switch
            checked={ebook.readerControls[option.key]}
            disabled={saving !== null}
            onChange={(event) => void toggle(option.key, event.target.checked)}
          />}
          label={<>
            <Typography fontWeight={700}>{__(option.label)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {__(option.description)}
            </Typography>
          </>}
        />
      ))}
    </FormGroup>
  </>;
}
